import * as React from "react";
import {
    createContext,
    Dispatch,
    FC,
    SetStateAction,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState
} from "react";
import { Feature } from "ol";
import { IFieldInfos } from "../Map/rest";
import { IRelatedDocs, IUrlParams } from "../interfaces";
import { Basemaps } from "../Map/enums";
import { generateSnapshots } from "../Map/snapshot";
import { IFilters } from "../Map/filters";
import { defaultFilterValues, defaultWindFarmFilterValues, objectIdGap } from "../Map/config";

export interface INomaContext {
    basemap: Basemaps;
    setBasemap: Dispatch<SetStateAction<Basemaps>>;
    activeTab: number;
    setActiveTab: Dispatch<SetStateAction<number>>;
    fieldInfos: IFieldInfos;
    setFieldInfos: Dispatch<SetStateAction<IFieldInfos>>;
    isMobile: boolean;
    isIFrame: boolean;
    identifiedFeature: Feature;
    setIdentifiedFeature: Dispatch<SetStateAction<Feature>>;
    previewFeature: Feature;
    setPreviewFeature: Dispatch<SetStateAction<Feature>>;
    locateFeature: ILocatedFeature;
    setLocateFeature: Dispatch<SetStateAction<Feature>>;
    applyFeature: IApplyFeature;
    setApplyFeature: Dispatch<SetStateAction<IApplyFeature>>;
    getFieldAlias: (fieldName: string, layerId?: string) => string;
    getFieldValue: (feature: Feature, fieldName: string, layerId?: string) => any;
    getDateFormated: (time: number) => string;
    downloadsPopupDocs: IRelatedDocs;
    setDownloadsPopupDocs: Dispatch<SetStateAction<IRelatedDocs>>;
    zoomToSourceFeatures: number;
    setZoomToSourceFeatures: Dispatch<SetStateAction<number>>;
    layersMenuVisible: boolean;
    setLayersMenuVisible: Dispatch<SetStateAction<boolean>>;
    toggleLayersMenuVisible: () => void;
    enabledLayers: string[];
    setEnabledLayers: Dispatch<SetStateAction<string[]>>;
    registerFeature: Feature;
    setRegisterFeature: Dispatch<SetStateAction<Feature>>;
    snapshotBig: string;
    setSnapshotBig: Dispatch<SetStateAction<string>>;
    snapshotSmall: string;
    setSnapshotSmall: Dispatch<SetStateAction<string>>;
    urlParams: IUrlParams;
    setUrlParams: Dispatch<SetStateAction<IUrlParams>>;
    filterDefaultValues: IFilters;
    setFilterDefaultValues: Dispatch<SetStateAction<IFilters>>;
    sideViewVisible: boolean;
    setSideViewVisible: Dispatch<SetStateAction<boolean>>;
}

export interface IApplyFeature {
    feature: Feature;
    isRight?: boolean;
}
export interface ILocatedFeature {
    feature: Feature;
    timestamp: number;
}

const getUrlParameters = () => {
    const url = new URL(window.location.href);
    const params = new URLSearchParams(url.search.slice(1));

    let objectId;
    let layerId = "0";
    params.forEach((value, key) => {
        if (key.toLowerCase() === "objectid" && value) {
            objectId = value;
        }
        if (key.toLowerCase() === "layerid" && value) {
            layerId = value;
        }
    });

    return (
        objectId && {
            objectId: (Number(objectId) + Number(layerId) * objectIdGap).toString(),
            layerId
        }
    );
};

const urlParameters: IUrlParams = getUrlParameters();

const getFilterDefaultValues = () =>
    urlParameters && urlParameters.layerId && urlParameters.layerId === "1"
        ? defaultWindFarmFilterValues
        : defaultFilterValues;

const iniFrame = () => {
    try {
        return window.self !== window.top;
    } catch (e) {
        return true;
    }
};

const isMobile =
    navigator.maxTouchPoints > 0 || navigator.platform === "iPhone" || navigator.platform === "iPad" || iniFrame();

const isIFrame = iniFrame();

const NomaContext = createContext<INomaContext>(null);

export const useNomaContext = (): INomaContext => useContext(NomaContext);

export const NomaContextProvider: FC = (props: React.PropsWithChildren<INomaContext>) => {
    const [basemap, setBasemap] = useState<Basemaps>(Basemaps.Mamma);
    const [activeTab, setActiveTab] = useState<number>(1);
    const [fieldInfos, setFieldInfos] = useState<IFieldInfos>();
    const [identifiedFeature, setIdentifiedFeature] = useState<Feature>(null);
    const [previewFeature, setPreviewFeature] = useState<Feature>(null);
    const [locateFeature, setLocateFeature] = useState<ILocatedFeature>(null);
    const [applyFeature, setApplyFeature] = useState<IApplyFeature>(null);
    const [downloadsPopupDocs, setDownloadsPopupDocs] = useState<IRelatedDocs>(null);
    const [zoomToSourceFeatures, setZoomToSourceFeatures] = useState<number>(null);
    const [layersMenuVisible, setLayersMenuVisible] = useState<boolean>(false);
    const [enabledLayers, setEnabledLayers] = useState<string[]>([]);
    const [registerFeature, setRegisterFeature] = useState<Feature>();
    const [snapshotBig, setSnapshotBig] = useState<string>();
    const [snapshotSmall, setSnapshotSmall] = useState<string>();
    const [urlParams, setUrlParams] = useState<IUrlParams>(urlParameters);
    const [filterDefaultValues, setFilterDefaultValues] = useState<IFilters>(getFilterDefaultValues());
    const [sideViewVisible, setSideViewVisible] = useState<boolean>(!isMobile || !isIFrame);

    const layersMenuVisibleRef = useRef<boolean>(false);
    const getFieldAlias = (fieldName: string, layerId?: string) =>
        fieldInfos && fieldInfos[layerId || "0"].find((f) => f.name === fieldName).alias;

    const getFieldValue = (feature: Feature, fieldName: string, layerId: string) => {
        const field = fieldInfos && fieldInfos[layerId || "0"].find((f) => f.name === fieldName);
        if (!field) {
            return undefined;
        }
        const value = feature.get(field.name);
        if (fieldName === "EVENTDATE" && value) {
            return getDateFormated(value) + " " + getTimeFormated(value);
        }
        if (field.type === "esriFieldTypeDate" && value) {
            return getDateFormated(value);
        }
        if (field.domain && field.domain.type === "codedValue" && value) {
            const codedValue = field.domain.codedValues.find((codedValue) => codedValue.code === value);
            return codedValue && codedValue.name ? codedValue.name : null;
        }
        return value;
    };

    const getDateFormated = (value: number) => {
        const date = new Date(value);
        return (
            date.getUTCDate().toString().padStart(2, "0") +
            "." +
            (date.getUTCMonth() + 1).toString().padStart(2, "0") +
            "." +
            date.getUTCFullYear()
        );
    };

    const toggleLayersMenuVisible = () => {
        layersMenuVisibleRef.current = !layersMenuVisibleRef.current;
        setLayersMenuVisible(layersMenuVisibleRef.current);
    };

    const changeActiveTab = (tab: number) => {
        setIdentifiedFeature(null);
        setPreviewFeature(null);
        setApplyFeature(null);
        setDownloadsPopupDocs(null);

        setActiveTab(tab);
    };

    const changeIdentifiedFeature = (feature: Feature) => {
        if (feature) {
            const objectId = feature && feature.get("OBJECTID");
            previewFeature && previewFeature.get("OBJECTID") !== objectId && setPreviewFeature(null);
            applyFeature && applyFeature.feature.get("OBJECTID") !== objectId && setApplyFeature(null);
            downloadsPopupDocs && downloadsPopupDocs.objectId !== objectId && setDownloadsPopupDocs(null);
        }
        setIdentifiedFeature(feature);
    };

    const changePreviewFeature = (feature: Feature) => {
        if (feature) {
            const objectId = feature && feature.get("OBJECTID");
            identifiedFeature && identifiedFeature.get("OBJECTID") !== objectId && setIdentifiedFeature(null);
            applyFeature && applyFeature.feature.get("OBJECTID") !== objectId && setApplyFeature(null);
            downloadsPopupDocs && downloadsPopupDocs.objectId !== objectId && setDownloadsPopupDocs(null);
            generateSnapshots(feature, basemap, setSnapshotBig, setSnapshotSmall);
        }
        setPreviewFeature(feature);
    };

    const changeApplyPopupFeature = (applyFeature: IApplyFeature) => {
        if (applyFeature && applyFeature.feature) {
            const objectId = applyFeature.feature && applyFeature.feature.get("OBJECTID");
            identifiedFeature && identifiedFeature.get("OBJECTID") !== objectId && setIdentifiedFeature(null);
            previewFeature && previewFeature.get("OBJECTID") !== objectId && setPreviewFeature(null);
            downloadsPopupDocs && downloadsPopupDocs.objectId !== objectId && setDownloadsPopupDocs(null);
            setApplyFeature({ feature: applyFeature.feature, isRight: applyFeature.isRight });
        } else {
            setApplyFeature(null);
        }
    };

    const changeDownloadsPopupDocs = (docs: IRelatedDocs) => {
        if (docs) {
            const objectId = docs.objectId;
            identifiedFeature && identifiedFeature.get("OBJECTID") !== objectId && setIdentifiedFeature(null);
            previewFeature && previewFeature.get("OBJECTID") !== objectId && setPreviewFeature(null);
            applyFeature && applyFeature.feature.get("OBJECTID") !== objectId && setApplyFeature(null);
        }
        setDownloadsPopupDocs(docs);
    };

    const changeRegisterFeature = (feature: Feature) => {
        if (feature) {
            setIdentifiedFeature(null);
            setPreviewFeature(null);
            setDownloadsPopupDocs(null);
            setApplyFeature(null);
            generateSnapshots(feature, basemap, setSnapshotBig, setSnapshotSmall);
        }
        setRegisterFeature(feature);
    };

    const prepareLocateFeature = useCallback((feature) => {
        setLocateFeature({ feature, timestamp: Date.now() });
    }, []);

    const values = {
        basemap,
        setBasemap,
        activeTab,
        setActiveTab: changeActiveTab,
        fieldInfos,
        setFieldInfos,
        identifiedFeature,
        setIdentifiedFeature: changeIdentifiedFeature,
        previewFeature,
        setPreviewFeature: changePreviewFeature,
        locateFeature,
        setLocateFeature: prepareLocateFeature,
        applyFeature,
        setApplyFeature: changeApplyPopupFeature,
        isMobile,
        isIFrame,
        getFieldAlias,
        getFieldValue,
        getDateFormated,
        downloadsPopupDocs,
        setDownloadsPopupDocs: changeDownloadsPopupDocs,
        zoomToSourceFeatures,
        setZoomToSourceFeatures,
        layersMenuVisible,
        setLayersMenuVisible,
        toggleLayersMenuVisible,
        enabledLayers,
        setEnabledLayers,
        registerFeature,
        setRegisterFeature: changeRegisterFeature,
        snapshotBig,
        setSnapshotBig,
        snapshotSmall,
        setSnapshotSmall,
        urlParams,
        setUrlParams,
        filterDefaultValues,
        setFilterDefaultValues,
        sideViewVisible,
        setSideViewVisible
    };

    return <NomaContext.Provider value={values}>{props.children}</NomaContext.Provider>;
};

const getTimeFormated = (value: number) => {
    const date = new Date(value);
    return date.getUTCHours().toString().padStart(2, "0") + ":" + date.getUTCMinutes().toString().padStart(2, "0");
};
